import React, { useState, useEffect } from 'react';
import { adminFetch } from '../../lib/adminFetch';
import AdminLogin from './AdminLogin';
import SeminarAdmin from './SeminarAdmin';
import PartnerAdmin from './PartnerAdmin';
import TestimonialAdmin from './TestimonialAdmin';
import DownloadAdmin from './DownloadAdmin';
import FileManager from './FileManager';
import MailAdmin from './MailAdmin';
import LogoAdmin from './LogoAdmin';
import CRMAdmin from './CRMAdmin';
import BookingAdmin from './BookingAdmin';
import MLAdmin from './MLAdmin';
import EventAdmin from './EventAdmin';
import ScheduleAdmin from './ScheduleAdmin';
import VideoAdmin from './VideoAdmin';
import SettingsAdmin from './SettingsAdmin';
import ReportAdmin from './ReportAdmin';
import ManualView from './ManualView';
import PasskeyAdmin from './PasskeyAdmin';
import AuditLogAdmin from './AuditLogAdmin';

const topNavItems = [
  { id: 'hp', label: 'ホームページ' },
  { id: 'mail', label: 'メール' },
  { id: 'event', label: 'イベント管理' },
  { id: 'crm', label: '顧客管理' },
  { id: 'booking', label: '予約管理' },
  { id: 'report', label: 'レポート' },
  { id: 'audit', label: '監査ログ' },
];
// 監査ログを見れるのはこのメンバーのみ（サーバー側でも同じ制限あり：public/api/_audit_log.php）
const AUDIT_LOG_VIEWERS = [
  'dt@enchantedlab.world',        // 玉置　大輔
  'shotaro@tsukamoto-office.com', // 塚本　祥太郎
  'yumenotanelabo@gmail.com',     // 林　美保子
  'c.nagano0808@gmail.com',       // 長野
];

const reportMenuItems = [
  { id: 'report_manual', label: '📖 操作マニュアル' },
  { id: 'report_customers', label: '顧客管理' },
  { id: 'report_import', label: 'CSV取り込み' },
  { id: 'report_monthly', label: 'マンスリー集計' },
  { id: 'report_workforce', label: '組織人員構成分析' },
  { id: 'report_labor_cost', label: '人時生産性分析' },
  { id: 'report_labor_profit', label: '人件費予実管理' },
  { id: 'report_wage_analysis', label: '適正賃金分析' },
  { id: 'report_wage_scatter', label: '賃金散布図' },
  { id: 'report_print', label: '印刷' },
  { id: 'report_wage', label: '派遣賃金データ' },
  { id: 'report_min_wage', label: '地域別最低賃金' },
];

const hpMenuItems = [
  { id: 'seminars', label: 'セミナー管理' },
  { id: 'partners', label: 'パートナー事務所' },
  { id: 'logos', label: 'パートナーロゴ' },
  { id: 'testimonials', label: 'お客様の声' },
  { id: 'downloads', label: 'ダウンロード資料' },
  { id: 'files', label: 'ファイル管理' },
  { id: 'schedules', label: 'スケジュール管理' },
  { id: 'videos', label: '動画管理' },
];

const mailMenuItems = [
  { id: 'mail_inbox', label: 'お問い合わせ' },
  { id: 'ml_lists', label: 'MLリスト一覧' },
  { id: 'ml_compose', label: 'ML一斉送信' },
  { id: 'ml_history', label: 'ML送信履歴' },
];

const eventMenuItems = [
  { id: 'event_list', label: 'イベント一覧' },
  { id: 'event_members', label: '参加者管理' },
];

const crmMenuItems = [
  { id: 'crm_all', label: '顧客一覧' },
  { id: 'crm_seminar', label: 'セミナー参加者' },
  { id: 'crm_academy', label: 'アカデミー関連' },
  { id: 'crm_partner', label: 'パートナー候補' },
  { id: 'crm_inquiry', label: '問い合わせ中' },
  { id: 'crm_replied', label: '対応済み' },
];

const AdminDashboard = () => {
  const [authed, setAuthed] = useState(localStorage.getItem('admin_auth') === 'true');


  const getStateFromHash = () => {
    const hash = window.location.hash.replace('#', '');
    if (hash === 'booking') return { section: 'booking', page: 'booking' };
    if (hash === 'audit') return { section: 'audit', page: 'audit' };
    if (hash === 'settings') return { section: 'settings', page: 'settings' };
    const mailPages = mailMenuItems.map((m) => m.id);
    if (hash === 'mail' || mailPages.includes(hash)) return { section: 'mail', page: mailPages.includes(hash) ? hash : 'mail_inbox' };
    const eventPages = eventMenuItems.map((m) => m.id);
    if (hash === 'event' || eventPages.includes(hash)) return { section: 'event', page: eventPages.includes(hash) ? hash : 'event_list' };
    const crmPages = crmMenuItems.map((m) => m.id);
    if (hash === 'crm' || crmPages.includes(hash)) return { section: 'crm', page: crmPages.includes(hash) ? hash : 'crm_all' };
    const reportPages = reportMenuItems.map((m) => m.id);
    if (hash === 'report' || reportPages.includes(hash)) return { section: 'report', page: reportPages.includes(hash) ? hash : 'report_customers' };
    const hpPages = hpMenuItems.map((m) => m.id);
    if (hpPages.includes(hash)) return { section: 'hp', page: hash };
    return { section: 'hp', page: 'seminars' };
  };

  const initial = getStateFromHash();
  const [activeSection, setActiveSection] = useState(initial.section);
  const [activePage, setActivePage] = useState(initial.page);
  const [showPasskeyModal, setShowPasskeyModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [displayName, setDisplayName] = useState(localStorage.getItem('admin_name') || '');
  const [nameInput, setNameInput] = useState('');
  const [nameSaving, setNameSaving] = useState(false);

  // 表示名が未取得（旧セッション・ログイン直後等）なら取得しておく。未ログイン時は絶対に呼ばない
  // （adminFetchは401で自動リロードするため、ここを無条件にすると未ログイン画面が無限リロードする）
  useEffect(() => {
    if (!authed || displayName) return;
    adminFetch('/api/admin-auth.php?action=my-profile')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && d.success && d.name) { setDisplayName(d.name); localStorage.setItem('admin_name', d.name); } })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const saveDisplayName = async () => {
    if (!nameInput.trim()) return;
    setNameSaving(true);
    try {
      const res = await adminFetch('/api/admin-auth.php?action=update-my-name', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: nameInput.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        setDisplayName(data.name);
        localStorage.setItem('admin_name', data.name);
        setEditingName(false);
      }
    } catch {}
    setNameSaving(false);
  };

  useEffect(() => {
    const onHashChange = () => {
      const state = getStateFromHash();
      setActiveSection(state.section);
      setActivePage(state.page);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  useEffect(() => {
    document.title = '管理画面 | SrWith';
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'robots';
      document.head.appendChild(meta);
    }
    meta.content = 'noindex, nofollow';
    return () => { meta.content = ''; };
  }, []);

  const role = localStorage.getItem('admin_role');
  const isSuperAdmin = role === 'super_admin';
  const isPartner = role === 'partner_sharoushi';
  const isAdmin = role === 'super_admin' || role === 'system_admin';
  const myEmail = (localStorage.getItem('admin_email') || '').toLowerCase();
  const isAuditViewer = AUDIT_LOG_VIEWERS.includes(myEmail);
  const reportPageIds = reportMenuItems.map((m) => m.id);

  // view as（管理者が特定パートナーの視点で閲覧）
  const [viewAs, setViewAs] = useState(localStorage.getItem('view_as') || '');
  const [partnerList, setPartnerList] = useState([]);
  const partnerView = isPartner || (isAdmin && !!viewAs); // パートナー視点での表示中か
  const visibleTopNav = (partnerView ? topNavItems.filter((i) => i.id === 'report') : topNavItems)
    .filter((i) => i.id !== 'audit' || isAuditViewer);

  useEffect(() => {
    if (isAdmin) {
      adminFetch(`/api/admin-report.php?action=partners&t=${Date.now()}`)
        .then((r) => r.json()).then((d) => setPartnerList(Array.isArray(d) ? d : [])).catch(() => {});
    }
  }, [isAdmin]);

  // レポート共通の顧客選択（サイドバー下部で選択・全レポート画面に反映・保存）
  const [reportCustomer, setReportCustomer] = useState(localStorage.getItem('report_customer') || '');
  const [reportCustomers, setReportCustomers] = useState([]);
  const changeReportCustomer = (id) => {
    setReportCustomer(id);
    if (id) localStorage.setItem('report_customer', id); else localStorage.removeItem('report_customer');
  };
  useEffect(() => {
    if (activeSection !== 'report') return;
    adminFetch(`/api/admin-report.php?action=customers&t=${Date.now()}${viewAs ? `&view_as=${encodeURIComponent(viewAs)}` : ''}`)
      .then((r) => r.json())
      .then((d) => {
        const list = Array.isArray(d) ? d : [];
        setReportCustomers(list);
        // 選択中の顧客がリストに無ければ（view as切替・削除等）選択解除
        setReportCustomer((prev) => {
          if (prev && !list.some((c) => c.id === prev)) { localStorage.removeItem('report_customer'); return ''; }
          return prev;
        });
      })
      .catch(() => {});
  }, [activeSection, activePage, viewAs]);

  const changeViewAs = (email) => {
    setViewAs(email);
    if (email) localStorage.setItem('view_as', email); else localStorage.removeItem('view_as');
    setActiveSection('report');
    setActivePage('report_customers');
    window.location.hash = 'report_customers';
  };

  // パートナー視点（本人 or view-as中）はレポート管理のみ。他セクションを開いていたらレポートへ戻す。
  useEffect(() => {
    if (partnerView && !reportPageIds.includes(activePage)) {
      setActiveSection('report');
      setActivePage('report_customers');
      window.location.hash = 'report_customers';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!authed) {
    return <AdminLogin onLogin={() => {
      localStorage.setItem('admin_auth', 'true');
      setAuthed(true);
    }} />;
  }

  const handleLogout = () => {
    localStorage.removeItem('admin_auth');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_role');
    localStorage.removeItem('admin_email');
    localStorage.removeItem('view_as');
    setAuthed(false);
  };

  // タイトルクリックで強制ハードリフレッシュ（キャッシュをクリアして再読み込み）
  const hardRefresh = async () => {
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch {}
    // キャッシュを回避して最新を取得
    window.location.reload();
  };

  const switchSection = (id) => {
    setActiveSection(id);
    if (id === 'hp') { setActivePage('seminars'); window.location.hash = 'seminars'; }
    if (id === 'mail') { setActivePage('mail_inbox'); window.location.hash = 'mail_inbox'; }
    if (id === 'event') { setActivePage('event_list'); window.location.hash = 'event_list'; }
    if (id === 'crm') { setActivePage('crm_all'); window.location.hash = 'crm_all'; }
    if (id === 'booking') { setActivePage('booking'); window.location.hash = 'booking'; }
    if (id === 'report') { setActivePage('report_customers'); window.location.hash = 'report_customers'; }
    if (id === 'audit') { setActivePage('audit'); window.location.hash = 'audit'; }
  };

  const renderPage = () => {
    if (partnerView && !reportPageIds.includes(activePage)) {
      return <div className="bg-white rounded-xl shadow p-8 text-gray-500">この画面を表示する権限がありません。</div>;
    }
    switch (activePage) {
      case 'mail_inbox': return <MailAdmin />;
      case 'ml_lists': return <MLAdmin initialTab="lists" />;
      case 'ml_compose': return <MLAdmin initialTab="compose" />;
      case 'ml_history': return <MLAdmin initialTab="history" />;
      case 'seminars': return <SeminarAdmin />;
      case 'partners': return <PartnerAdmin />;
      case 'testimonials': return <TestimonialAdmin />;
      case 'logos': return <LogoAdmin />;
      case 'downloads': return <DownloadAdmin />;
      case 'files': return <FileManager />;
      case 'schedules': return <ScheduleAdmin />;
      case 'videos': return <VideoAdmin />;
      case 'event_list': return <EventAdmin page="event_list" onNavigate={(p) => { setActivePage(p); window.location.hash = p; }} />;
      case 'event_members': return <EventAdmin page="event_members" onNavigate={(p) => { setActivePage(p); window.location.hash = p; }} />;
      case 'crm_all': return <CRMAdmin filter="all" />;
      case 'crm_seminar': return <CRMAdmin filter="セミナー" />;
      case 'crm_academy': return <CRMAdmin filter="アカデミー" />;
      case 'crm_partner': return <CRMAdmin filter="パートナー" />;
      case 'crm_inquiry': return <CRMAdmin filter="未対応" />;
      case 'crm_replied': return <CRMAdmin filter="対応済み" />;
      case 'booking': return <BookingAdmin />;
      case 'audit': return isAuditViewer ? <AuditLogAdmin /> : <div className="bg-white rounded-xl shadow p-8 text-gray-500">この画面を閲覧する権限がありません。</div>;
      case 'report_manual': return <ManualView />;
      case 'report_customers': return <ReportAdmin page="report_customers" viewAs={viewAs} customerId={reportCustomer} />;
      case 'report_import': return <ReportAdmin page="report_import" viewAs={viewAs} customerId={reportCustomer} />;
      case 'report_monthly': return <ReportAdmin page="report_monthly" viewAs={viewAs} customerId={reportCustomer} />;
      case 'report_wage': return <ReportAdmin page="report_wage" viewAs={viewAs} customerId={reportCustomer} />;
      case 'report_workforce': return <ReportAdmin page="report_workforce" viewAs={viewAs} customerId={reportCustomer} />;
      case 'report_labor_cost': return <ReportAdmin page="report_labor_cost" viewAs={viewAs} customerId={reportCustomer} />;
      case 'report_labor_profit': return <ReportAdmin page="report_labor_profit" viewAs={viewAs} customerId={reportCustomer} />;
      case 'report_wage_analysis': return <ReportAdmin page="report_wage_analysis" viewAs={viewAs} customerId={reportCustomer} />;
      case 'report_wage_scatter': return <ReportAdmin page="report_wage_scatter" viewAs={viewAs} customerId={reportCustomer} />;
      case 'report_print': return <ReportAdmin page="report_print" viewAs={viewAs} customerId={reportCustomer} />;
      case 'report_min_wage': return <ReportAdmin page="report_min_wage" viewAs={viewAs} customerId={reportCustomer} />;
      case 'settings': return isAdmin ? <SettingsAdmin /> : (
        <div className="bg-white rounded-xl shadow p-8 text-gray-500">この画面は管理者のみ利用できます。</div>
      );
      default: return null;
    }
  };

  const getSidebarItems = () => {
    if (activeSection === 'hp') return hpMenuItems;
    if (activeSection === 'mail') return mailMenuItems;
    if (activeSection === 'event') return eventMenuItems;
    if (activeSection === 'crm') return crmMenuItems;
    if (activeSection === 'report') return partnerView ? reportMenuItems.filter((m) => m.id !== 'report_wage' && m.id !== 'report_min_wage' && m.id !== 'report_labor_profit') : reportMenuItems;
    return null;
  };

  const sidebarItems = getSidebarItems();

  return (
    <div className="h-screen bg-gray-100 flex flex-col">
      {/* トップナビ */}
      <header className="bg-gray-900 text-white px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-8">
          <h1 onClick={hardRefresh} title="クリックでハードリフレッシュ（キャッシュをクリアして再読み込み）"
            className="flex items-center gap-2 text-lg font-bold shrink-0 cursor-pointer select-none hover:text-gray-300 transition-colors">
            <img src="/images/srwith-logo.png" alt="SrWith" className="h-6 w-auto shrink-0" />
            管理
          </h1>
          <nav className="flex gap-1 overflow-x-auto">
            {visibleTopNav.map((item) => (
              <button
                key={item.id}
                onClick={() => switchSection(item.id)}
                className={`px-4 py-1.5 rounded text-sm font-bold whitespace-nowrap shrink-0 transition-colors ${
                  activeSection === item.id
                    ? 'bg-gray-700 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800'
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>
        </div>
        <div className="flex items-center gap-4">
          {isAdmin && (
            partnerList.length > 0 ? (
              <select value={viewAs} onChange={(e) => changeViewAs(e.target.value)}
                title="View As：選んだパートナー社労士の見え方（担当顧客のみ）で管理画面を確認できます"
                className={`text-sm rounded px-2 py-1 border-none focus:outline-none ${viewAs ? 'bg-amber-500 text-white font-bold' : 'bg-gray-700 text-gray-200'}`}>
                <option value="">通常表示</option>
                {partnerList.map((p) => { const email = typeof p === 'string' ? p : p.email; const label = typeof p === 'string' ? p : (p.name || p.email); return <option key={email} value={email}>view as: {label}</option>; })}
              </select>
            ) : (
              <select disabled value=""
                title="View As：パートナー社労士を登録すると、その担当者の見え方（担当顧客のみ）で確認できます。登録は「設定」から。"
                className="text-sm rounded px-2 py-1 border-none bg-gray-800 text-gray-500 cursor-not-allowed">
                <option value="">👁 View As（社労士未登録）</option>
              </select>
            )
          )}
          <a href="/academy/members" target="_blank" rel="noopener noreferrer" title="アカデミー会員ページを開く" aria-label="アカデミー会員ページを開く"
            className="text-lg text-gray-400 hover:text-white transition-colors leading-none">🎓</a>
          <a href="/" target="_blank" rel="noopener noreferrer" title="ホームページを開く" aria-label="ホームページを開く"
            className="text-lg text-gray-400 hover:text-white transition-colors leading-none">🏠</a>
          {isAdmin && (
            <button onClick={() => { setActiveSection('settings'); setActivePage('settings'); window.location.hash = 'settings'; }}
              className="text-sm text-gray-400 hover:text-white transition-colors">設定</button>
          )}
          <div className="relative">
            <button onClick={() => { setShowUserMenu((v) => !v); setEditingName(false); }}
              className="text-sm text-gray-200 hover:text-white transition-colors font-bold flex items-center gap-1">
              👤 {displayName || localStorage.getItem('admin_email') || 'ユーザー'}
              <span className="text-[10px] text-gray-500">▾</span>
            </button>
            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg text-gray-800 z-50 overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="p-3 border-b border-gray-100">
                  {editingName ? (
                    <div className="flex gap-1.5">
                      <input type="text" value={nameInput} onChange={(e) => setNameInput(e.target.value)} autoFocus
                        placeholder="表示名"
                        className="flex-1 min-w-0 border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-pink-dark" />
                      <button onClick={saveDisplayName} disabled={nameSaving || !nameInput.trim()}
                        className="bg-pink-dark text-white px-2.5 py-1 rounded text-xs font-bold hover:bg-brown disabled:opacity-50 shrink-0">保存</button>
                    </div>
                  ) : (
                    <button onClick={() => { setNameInput(displayName); setEditingName(true); }}
                      className="w-full text-left text-sm hover:text-pink-dark">
                      <span className="text-[11px] text-gray-400 block">表示名</span>
                      {displayName || '（未設定・クリックして設定）'}
                    </button>
                  )}
                </div>
                <button onClick={() => { setShowPasskeyModal(true); setShowUserMenu(false); }}
                  className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 border-b border-gray-100">🔑 パスキー</button>
                <button onClick={handleLogout} className="w-full text-left px-3 py-2.5 text-sm text-red-600 hover:bg-red-50">ログアウト</button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* view as バナー */}
      {isAdmin && viewAs && (
        <div className="bg-amber-500 text-white px-6 py-2 flex items-center justify-between text-sm">
          <span>👁 <b>{viewAs}</b> として表示中（パートナー社労士の見え方・担当顧客のみ）</span>
          <button onClick={() => changeViewAs('')} className="underline font-bold hover:text-amber-100">通常表示に戻る</button>
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
        {/* 左サイドバー（画面に固定・メイン側だけスクロール） */}
        {sidebarItems && (
          <aside className="w-52 bg-gray-800 text-white flex flex-col shrink-0">
            <nav className="flex-1 py-4 overflow-y-auto">
              {sidebarItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => { setActivePage(item.id); window.location.hash = item.id; }}
                  className={`w-full text-left px-5 py-3 text-sm whitespace-nowrap transition-colors ${
                    activePage === item.id
                      ? 'bg-gray-700 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>
            {activeSection === 'report' && (
              <div className="p-4 border-t border-gray-700">
                <label className="block text-[11px] text-gray-400 mb-1.5">顧客（全レポート共通）</label>
                <select value={reportCustomer} onChange={(e) => changeReportCustomer(e.target.value)}
                  className="w-full bg-gray-700 text-white text-sm rounded-lg px-2 py-2 focus:outline-none focus:ring-2 focus:ring-pink-dark">
                  <option value="">— 顧客を選択 —</option>
                  {reportCustomers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
          </aside>
        )}

        {/* メインコンテンツ */}
        <main className="flex-1 p-8 overflow-y-auto">
          {renderPage()}
        </main>
      </div>
      {showPasskeyModal && <PasskeyAdmin onClose={() => setShowPasskeyModal(false)} />}
    </div>
  );
};

export default AdminDashboard;
